import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import {
  updateFinancialTransactionData,
  getShowFinancialTransaction,
  getFinancialTransactionsData,
} from "@/Api/financialTransactions";
import { selectExpenseTypesData } from "@/Api/expenseTypes";
import { selectRevenueTypesData } from "@/Api/revenueTypes";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExpenseTypeProps } from "@/types/expenseType";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mask } from "@/utils/mask";
import { getSelectUnitiesOnlyActives } from "@/Api/unities";
import { PAYMENT_FORMS } from "@/utils/constants";
import { getSelectBankAccounts } from "@/Api/bankAccounts";

const PAYMENT_FORMS_LIST = [
  { id: PAYMENT_FORMS.MONEY, name: "Dinheiro" },
  { id: PAYMENT_FORMS.CREDIT_CARD, name: "Cartão" },
  { id: PAYMENT_FORMS.PIX, name: "Pix Sicredi" },
  { id: PAYMENT_FORMS.DEPOSIT, name: "Depósito" },
];

const schema = z.object({
  description: z
    .string({ message: "*requerido" })
    .min(1, "*requerido")
    .max(255, "Máximo 255 caracteres"),
  amount: z.string({ message: "*requerido" }),
  // Vira number no onChange (`field.onChange(Number(value))`), não string.
  unity_id: z.any().refine((val) => val !== undefined && val !== null && val !== "", {
    message: "*requerido",
  }),
  expense_type_id: z.any().optional(),
  revenue_type_id: z.any().optional(),
  due_date: z.string({ message: "*requerido" }),
  default_due_date: z.string().optional(),
  competence_date: z.string({ message: "*requerido" }),
  recurrence_period_type_id: z.any().optional(),
  payment_date: z.string().optional(),
  observation: z.string().optional(),
  financial_transaction_type_id: z.any().optional(),
  financial_transaction_status_id: z.any().optional(),
  payment_form_id: z.any().optional(),
  bank_account_id: z.any().optional(),
});

type SchemaType = z.infer<typeof schema>;

export const FINANCIAL_TRANSACTION_STATUS = {
  OPEN: 1,
  PAID: 2,
  CANCELED: 3,
  EXPIRED: 4,
};

export const FINANCIAL_TRANSACTION_TYPE = {
  ACCOUNTS_PAYABLE: 1,
  ACCOUNTS_RECEIVABLE: 2,
};

export const RECURRENCE_PERIOD_TYPE = {
  DAILY: 1,
  WEEKLY: 2,
  MONTHLY: 3,
  BIMONTHLY: 4,
  QUARTERLY: 5,
  SEMIANNUAL: 6,
  ANNUAL: 7,
};

const TRANSACTION_STATUS = [
  { id: FINANCIAL_TRANSACTION_STATUS.OPEN, name: "Em Aberto" },
  { id: FINANCIAL_TRANSACTION_STATUS.PAID, name: "Pago" },
  { id: FINANCIAL_TRANSACTION_STATUS.CANCELED, name: "Cancelado" },
  { id: FINANCIAL_TRANSACTION_STATUS.EXPIRED, name: "Vencido" },
];

const TRANSACTION_TYPES = [
  { id: FINANCIAL_TRANSACTION_TYPE.ACCOUNTS_PAYABLE, name: "Contas a Pagar" },
  {
    id: FINANCIAL_TRANSACTION_TYPE.ACCOUNTS_RECEIVABLE,
    name: "Contas a Receber",
  },
];

const RECURRENCE_TYPES = [
  { id: RECURRENCE_PERIOD_TYPE.DAILY, name: "Diário" },
  { id: RECURRENCE_PERIOD_TYPE.WEEKLY, name: "Semanal" },
  { id: RECURRENCE_PERIOD_TYPE.MONTHLY, name: "Mensal" },
  { id: RECURRENCE_PERIOD_TYPE.BIMONTHLY, name: "Bimestral" },
  { id: RECURRENCE_PERIOD_TYPE.QUARTERLY, name: "Trimestral" },
  { id: RECURRENCE_PERIOD_TYPE.SEMIANNUAL, name: "Semestral" },
  { id: RECURRENCE_PERIOD_TYPE.ANNUAL, name: "Anual" },
];

export const UpdateFinancialTransactionBtn = ({
  updateFront,
  uuid,
}: {
  updateFront: React.Dispatch<React.SetStateAction<any>>;
  uuid: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const [expenses, setExpenses] = useState<ExpenseTypeProps[]>([]);
  const [revenueTypes, setRevenueTypes] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [selectedTransactionType, setSelectedTransactionType] = useState(
    FINANCIAL_TRANSACTION_TYPE.ACCOUNTS_PAYABLE
  );
  const [selectedPaymentForm, setSelectedPaymentForm] = useState<
    number | undefined
  >(undefined);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      description: "",
      amount: "",
      unity_id: undefined,
      expense_type_id: undefined,
      revenue_type_id: undefined,
      due_date: "",
      competence_date: "",
      recurrence_period_type_id: undefined,
      payment_date: "",
      observation: "",
      financial_transaction_type_id:
        FINANCIAL_TRANSACTION_TYPE.ACCOUNTS_PAYABLE,
      financial_transaction_status_id: FINANCIAL_TRANSACTION_STATUS.OPEN,
      payment_form_id: undefined,
      bank_account_id: undefined,
    },
  });
  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        setFormState("loading");
        try {
          const expensesResponse = await selectExpenseTypesData();
          if (expensesResponse.data?.response) {
            setExpenses(expensesResponse?.data?.response);
          }

          const revenueResponse = await selectRevenueTypesData();
          if (revenueResponse.data?.response) {
            setRevenueTypes(revenueResponse.data.response);
          }

          const unitsResponse = await getSelectUnitiesOnlyActives();
          if (unitsResponse.data?.response) {
            setUnits(unitsResponse.data.response);
          }

          const bankAccountsResponse = await getSelectBankAccounts();
          if (bankAccountsResponse.data?.response) {
            setBankAccounts(bankAccountsResponse?.data?.response);
          }

          const { data: responseApi } = await getShowFinancialTransaction(uuid);
          const transaction = responseApi?.response;

          if (transaction) {
            form.setValue("description", transaction.description || "");
            form.setValue(
              "amount",
              Mask.formatCurrency(transaction.amount || 0)
            );
            form.setValue("unity_id", transaction.unity_id);
            form.setValue("expense_type_id", transaction.expense_type_id);
            form.setValue("revenue_type_id", transaction.revenue_type_id);

            if (transaction?.due_date) {
              const dueDate = transaction.due_date.split("T")?.[0].split(" ")?.[0];
              form.setValue("due_date", dueDate || "");
            } else {
              form.setValue("due_date", transaction.due_date || "");
            }

            if (transaction?.competence_date) {
              const competenceDate = transaction.competence_date.split("T")?.[0].split(" ")?.[0];
              console.log('competenceDate', competenceDate);
              form.setValue("competence_date", competenceDate || "");
            } else {
              form.setValue("competence_date", "");
            }
            form.setValue(
              "recurrence_period_type_id",
              transaction.recurrence_period_type_id
            );
            form.setValue("payment_form_id", transaction?.payment_form_id);
            form.setValue("bank_account_id", transaction?.bank_account_id);

            if (transaction.payment_date) {
              const paymentDate = transaction.payment_date.split("T")[0].split(" ")[0];
              form.setValue("payment_date", paymentDate);
            } else {
              form.setValue("payment_date", "");
            }

            form.setValue("observation", transaction.observation || "");
            form.setValue(
              "financial_transaction_type_id",
              transaction.financial_transaction_type_id
            );
            form.setValue(
              "financial_transaction_status_id",
              transaction.financial_transaction_status_id
            );

            setSelectedTransactionType(
              transaction.financial_transaction_type_id
            );
          }

          setFormState("default");
        } catch (error) {
          console.error("Erro ao carregar dados:", error);
          toast({
            description: "Erro ao carregar dados",
            action: <Check color="#ff0000" />,
          });
          setFormState("error");
          setTimeout(() => {
            setFormState("default");
          }, 1000);
        }
      }
    };

    fetchData();
  }, [isOpen, uuid, form, toast]);

  useEffect(() => {
    if (
      selectedTransactionType === FINANCIAL_TRANSACTION_TYPE.ACCOUNTS_PAYABLE
    ) {
      form.setValue("revenue_type_id", undefined);
      form.setValue("payment_form_id", undefined);
      form.setValue("bank_account_id", undefined);
    } else {
      form.setValue("expense_type_id", undefined);
      form.setValue("payment_form_id", undefined);
      form.setValue("bank_account_id", undefined);
    }
  }, [selectedTransactionType, form]);

  useEffect(() => {
    if (selectedPaymentForm !== PAYMENT_FORMS.DEPOSIT) {
      form.setValue("bank_account_id", undefined);
    }
  }, [selectedPaymentForm, form]);

  const handleOpenUpdate = () => {
    setIsOpen(true);
  };

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");

    const payload = {
      description: data.description,
      amount: data.amount
        ? parseFloat(Mask.transformCurrencyInFloat(data.amount))
        : 0,
      unity_id: data.unity_id,
      expense_type_id: data.expense_type_id,
      revenue_type_id: data.revenue_type_id,
      due_date: data.due_date,
      competence_date: data.competence_date,
      recurrence_period_type_id: data.recurrence_period_type_id || undefined,
      payment_date: data.payment_date || "",
      observation: data.observation || "",
      financial_transaction_type_id: data.financial_transaction_type_id,
      financial_transaction_status_id: data.financial_transaction_status_id,
      payment_form_id: data.payment_form_id || undefined,
      bank_account_id: data.bank_account_id || undefined,
    };

    try {
      const { data: apiResponse, ok } = await updateFinancialTransactionData(
        uuid,
        payload
      );
      if (!ok) {
        setFormState("error");
        setTimeout(() => {
          setFormState("default");
        }, 1000);
        setIsOpen(false);
        return;
      }

      const { data: freshData } = await getFinancialTransactionsData();
      if (freshData?.response) {
        updateFront(freshData.response);
      }

      toast({
        description:
          apiResponse?.message ||
          "Transação financeira atualizada com sucesso!",
        action: <Check color="#1edc2b" />,
      });

      form.reset();
      setFormState("default");
      setSelectedPaymentForm(undefined);
      setIsOpen(false);
    } catch (error) {
      console.error("Erro ao atualizar transação:", error);
      setFormState("error");
      setTimeout(() => {
        setFormState("default");
      }, 1000);
    }
  };

  // const showBankAccountField =
  //   selectedTransactionType === FINANCIAL_TRANSACTION_TYPE.ACCOUNTS_PAYABLE &&
  //   selectedPaymentForm === PAYMENT_FORMS.DEPOSIT;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          onClick={handleOpenUpdate}
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Editar Transação Financeira
          </DialogDescription>
          <DialogTitle className="text-2xl">
            Informações da Transação
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh]">
          <Form
            onSubmit={form.handleSubmit(formSubmit)}
            {...form}
            className="space-y-3"
          >
            <Form.Row>
              <Form.Field name="description">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Descrição <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="Descrição da transação" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <Form.Row>
              <Form.Field name="amount">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Valor <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input
                        typeMask="currency"
                        placeholder="0,00"
                        {...field}
                      />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Row>
                <Form.Field name="unity_id">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Unidade <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Select
                          onValueChange={(value) =>
                            field.onChange(Number(value))
                          }
                          value={field.value?.toString()}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione uma unidade" />
                          </SelectTrigger>
                          <SelectContent>
                            {units.map((unity) => (
                              <SelectItem
                                key={unity.id}
                                value={unity.id.toString()}
                              >
                                {unity.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>

              <Form.Row>
                <Form.Field name="financial_transaction_type_id">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Tipo de Transação <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(Number(value));
                            setSelectedTransactionType(Number(value));
                          }}
                          value={field.value?.toString()}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um tipo" />
                          </SelectTrigger>
                          <SelectContent>
                            {TRANSACTION_TYPES.map((type) => (
                              <SelectItem
                                key={type.id}
                                value={type.id.toString()}
                              >
                                {type.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedTransactionType ===
                FINANCIAL_TRANSACTION_TYPE.ACCOUNTS_PAYABLE && (
                <Form.Row>
                  <Form.Field name="expense_type_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Despesa <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Select
                            onValueChange={(value) =>
                              field.onChange(Number(value))
                            }
                            value={field.value?.toString()}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione uma despesa" />
                            </SelectTrigger>
                            <SelectContent>
                              {expenses.map((expense) => (
                                <SelectItem
                                  key={expense.id}
                                  value={expense.id.toString()}
                                >
                                  {expense.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              )}

              {selectedTransactionType ===
                FINANCIAL_TRANSACTION_TYPE.ACCOUNTS_RECEIVABLE && (
                <Form.Row>
                  <Form.Field name="revenue_type_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Tipo de Receita <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Select
                            onValueChange={(value) =>
                              field.onChange(Number(value))
                            }
                            value={field.value?.toString()}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione um tipo de receita" />
                            </SelectTrigger>
                            <SelectContent>
                              {revenueTypes.map((revenue) => (
                                <SelectItem
                                  key={revenue.id}
                                  value={revenue.id.toString()}
                                >
                                  {revenue.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              )}

              <Form.Row>
                <Form.Field name="financial_transaction_status_id">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Status <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Select
                          onValueChange={(value) =>
                            field.onChange(Number(value))
                          }
                          value={field.value?.toString()}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um status" />
                          </SelectTrigger>
                          <SelectContent>
                            {TRANSACTION_STATUS.map((status) => (
                              <SelectItem
                                key={status.id}
                                value={status.id.toString()}
                              >
                                {status.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>
            </div>

            {selectedTransactionType ===
              FINANCIAL_TRANSACTION_TYPE.ACCOUNTS_PAYABLE && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Row>
                  <Form.Field name="payment_form_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Forma de Pagamento <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Select
                            onValueChange={(value) => {
                              const numValue = Number(value);
                              field.onChange(numValue);
                              setSelectedPaymentForm(numValue);
                            }}
                            value={field.value?.toString()}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a forma de pagamento" />
                            </SelectTrigger>
                            <SelectContent>
                              {PAYMENT_FORMS_LIST.map((form) => (
                                <SelectItem
                                  key={form.id}
                                  value={form.id.toString()}
                                >
                                  {form.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
                <Form.Row>
                  <Form.Field name="bank_account_id">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Conta Bancária <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Select
                            onValueChange={(value) =>
                              field.onChange(Number(value))
                            }
                            value={field.value?.toString()}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a conta bancária" />
                            </SelectTrigger>
                            <SelectContent>
                              {bankAccounts.map((account) => (
                                <SelectItem
                                  key={account.id}
                                  value={account.id.toString()}
                                >
                                  {account?.surname}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Row>
                <Form.Field name="due_date">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Data de Vencimento <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Input type="date" {...field} />
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>

              <Form.Row>
                <Form.Field name="competence_date">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Data de Competência <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Input type="date" {...field} />
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Row>
                <Form.Field name="payment_date">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Data de Pagamento <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Input
                          type="date"
                          {...field}
                          value={field.value || ""}
                        />
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Row>
                <Form.Field name="recurrence_period_type_id">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Tipo de Recorrência <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Select
                          onValueChange={(value) =>
                            field.onChange(Number(value))
                          }
                          value={field.value?.toString()}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione (se aplicável)" />
                          </SelectTrigger>
                          <SelectContent>
                            {RECURRENCE_TYPES.map((type) => (
                              <SelectItem
                                key={type.id}
                                value={type.id.toString()}
                              >
                                {type.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>
            </div>

            <Form.Row>
              <Form.Field name="observation">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Observações <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Textarea
                        placeholder="Observações adicionais"
                        className="resize-none"
                        {...field}
                        value={field.value || ""}
                      />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <DialogFooter>
              <Button
                type="submit"
                className={cn("w-full", colors.bg_primary)}
                disabled={formState !== "default"}
              >
                {formState === "loading" && (
                  <>
                    Atualizando <LoaderSpin />
                  </>
                )}
                {formState === "default" && "Atualizar"}
                {formState === "error" && "Erro ao atualizar"}
              </Button>
            </DialogFooter>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
